import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FaSpinner, FaArrowLeft, FaArrowRight, FaTrophy, FaCalendarAlt, FaUserGraduate, FaSchool } from 'react-icons/fa';
import Header from '../common/Header';
import Footer from '../common/Footer';

const HowItWorksPage = () => {
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRatings: 0,
    hasNextPage: false,
    hasPrevPage: false
  });

  // استخدام React Query لجلب الإحصائيات
  const { 
    data: statisticsData, 
    isLoading: statisticsLoading, 
    error: statisticsError 
  } = useQuery({
    queryKey: ['statistics'],
    queryFn: async () => {
      const response = await fetch('https://question-manager-backend.onrender.com/api/statis');
      if (!response.ok) {
        throw new Error('فشل في جلب الإحصائيات');
      }
      return response.json();
    },
    staleTime: 5 * 60 * 1000, // 5 دقائق
    retry: 2
  });
  
  // استخدام React Query لجلب قائمة أفضل الطلاب
  const { 
    data: topStudentsData, 
    isLoading: topStudentsLoading, 
    error: topStudentsError 
  } = useQuery({
    queryKey: ['topStudents'],
    queryFn: async () => {
      const response = await fetch('https://question-manager-backend.onrender.com/api/result/leaderboard/top-students');
      if (!response.ok) {
        throw new Error('فشل في جلب قائمة أفضل الطلاب');
      }
      return response.json();
    },
    staleTime: 5 * 60 * 1000, // 5 دقائق
    retry: 2
  });

  const steps = [
    {
      number: '1',
      title: 'تسجيل الدخول',
      description:
        'قم بتسجيل الدخول إلى حسابك للوصول إلى جميع المراجعات والاختبارات بسهولة.',
    },
    {
      number: '2',
      title: 'ابدأ المراجعة',
      description:
        'اختر المادة وابدأ مراجعتها من خلال محتوى تفاعلي ومصادر تعليمية مميزة تساعدك على الفهم العميق.',
    },
    {
      number: '3',
      title: 'احصل على تقارير ونتائجك',
      description:
        'اطّلع على تقارير الأداء التفصيلية ونتائجك لتحديد نقاط القوة والتحسين.',
    },
  ];

  // الإحصائيات الديناميكية من API أو القيم الافتراضية
  const stats = statisticsData?.success ? [
    { 
      number: statisticsData.statistics.activeStudents, 
      label: statisticsData.statistics.labels.activeStudentsLabel 
    },
    { 
      number: statisticsData.statistics.weeklyReviews, 
      label: statisticsData.statistics.labels.weeklyReviewsLabel 
    },
    { 
      number: statisticsData.statistics.satisfactionRate, 
      label: statisticsData.statistics.labels.satisfactionRateLabel 
    },
    { 
      number: statisticsData.statistics.supportAvailability, 
      label: statisticsData.statistics.labels.supportAvailabilityLabel 
    }
  ] : [
    { number: '1600+', label: 'طالب نشط' },
    { number: '500+', label: 'مراجعة أسبوعية' },
    { number: '95%', label: 'نسبة الرضا' },
    { number: '24/7', label: 'دعم متواصل' }
  ];

  // دالة لجلب التعليقات من API
  const fetchRatings = async (page = 1, limit = 6) => {
    try {
      if (page === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const response = await fetch(`https://question-manager-backend.onrender.com/api/rating?page=${page}&limit=${limit}`);
      
      if (!response.ok) {
        throw new Error('فشل في جلب التعليقات');
      }

      const data = await response.json();
      
      // تصفية التعليقات لإظهار فقط تلك التي لديها isVisible: true
      const visibleRatings = (data.ratings || []).filter(rating => rating.isVisible === true);
      
      if (page === 1) {
        setRatings(visibleRatings);
      } else {
        setRatings(prev => [...prev, ...visibleRatings]);
      }
      
      setPagination(data.pagination || {
        currentPage: page,
        totalPages: 1,
        totalRatings: 0,
        hasNextPage: false,
        hasPrevPage: false
      });
      
    } catch (error) {
      console.error('Error fetching ratings:', error);
      if (page === 1) {
        setRatings([]);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // دالة لتحميل المزيد من التعليقات
  const loadMoreRatings = () => {
    if (pagination.hasNextPage && !loadingMore) {
      fetchRatings(pagination.currentPage + 1, 6);
    }
  };

  // دالة لتحميل الصفحة الأولى
  const loadFirstPage = () => {
    fetchRatings(1, 6);
  };

  // دالة لاستخراج الحرف الأول من اسم المستخدم
  const getInitial = (username) => {
    if (!username) return 'ط';
    // استخراج أول حرف عربي أو إرجاع 'ط' كافتراضي
    const arabicMatch = username.match(/[\u0600-\u06FF]/);
    return arabicMatch ? arabicMatch[0] : username.charAt(0).toUpperCase();
  };

  // دالة لتنسيق التاريخ
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('ar-SA', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }).format(date);
    } catch {
      return 'تاريخ غير معروف';
    }
  };

  // ألوان البطاقات (نفس الألوان الأصلية)
  const cardColors = [
    'from-blue-100 to-blue-200 text-blue-800',
    'from-green-100 to-green-200 text-green-800',
    'from-purple-100 to-purple-200 text-purple-800',
    'from-yellow-100 to-yellow-200 text-yellow-800',
    'from-red-100 to-red-200 text-red-800'
  ];

  // ألوان للمراكز في قائمة أفضل الطلاب
  const rankColors = {
    1: "text-yellow-600",
    2: "text-gray-600",
    3: "text-amber-700",
    4: "text-blue-600"
  };

  // ألوان خلفية بطاقات الطلاب
  const studentCardGradients = [
    'from-primary/5 to-primary/10',
    'from-blue-500/5 to-blue-500/10',
    'from-amber-500/5 to-amber-500/10',
    'from-purple-500/5 to-purple-500/10'
  ];

  useEffect(() => {
    fetchRatings(1, 6);
  }, []);

  // Skeleton Loading للإحصائيات
  const StatisticsSkeleton = () => (
    <div className="grid grid-cols-2 gap-6 mt-20 lg:grid-cols-4">
      {[...Array(4)].map((_, index) => (
        <div key={index} className="card-subtle p-6 rounded-2xl text-center">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-300 rounded-lg mb-2 mx-auto w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded mx-auto w-1/2"></div>
          </div>
        </div>
      ))}
    </div>
  );
  
  // Skeleton Loading للطلاب المتفوقين
  const TopStudentsSkeleton = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {[...Array(4)].map((_, index) => (
        <div key={index} className="card-subtle p-6 rounded-2xl">
          <div className="animate-pulse">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-gray-300 rounded-full mr-4"></div>
              <div>
                <div className="h-6 bg-gray-300 rounded-lg mb-2 w-24"></div>
                <div className="h-4 bg-gray-200 rounded w-32"></div>
              </div>
            </div>
            <div className="h-4 bg-gray-200 rounded mb-2 w-full"></div>
            <div className="h-4 bg-gray-200 rounded mb-2 w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 overflow-x-hidden" dir="rtl">
      {/* Header */}
      <Header />
      
      {/* How It Works Section */}
      <section id="how-it-works" className="relative py-16 sm:py-20 lg:py-28 bg-gray-50 grid-pattern">
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-dark-yellow opacity-5 rounded-full blur-3xl"></div>

        <div className="relative px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold leading-tight text-gray-900 sm:text-4xl lg:text-5xl">
              كيف تعمل <span className="text-primary">المنصة؟</span>
            </h2>
            <p className="max-w-lg mx-auto mt-4 text-lg leading-relaxed text-gray-600">
              خطوات بسيطة لبدء رحلة المراجعة والتحسين الأكاديمي
            </p>
          </div>

          <div className="relative mt-16 lg:mt-24">
            <div className="hidden lg:block absolute top-16 left-1/4 right-1/4 h-0.5 bg-gradient-to-r from-transparent via-primary to-transparent opacity-20"></div>

            <div className="grid grid-cols-1 text-center gap-12 md:grid-cols-3 gap-x-16">
              {steps.map((step, index) => (
                <div key={index} className="animate-fade-in-up" style={{ animationDelay: `${(index + 1) * 0.1}s` }}>
                  <div className="relative inline-flex items-center justify-center w-20 h-20 mx-auto bg-white border-4 border-primary rounded-full shadow-lg">
                    <span className="text-3xl font-bold text-primary">{step.number}</span>
                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-dark-yellow rounded-full animate-pulse"></div>
                  </div>
                  <h3 className="mt-8 text-2xl font-bold text-gray-900">{step.title}</h3>
                  <p className="mt-4 text-lg text-gray-600 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Statistics Section */}
          <div className="mt-20">
            <div className="max-w-2xl mx-auto text-center mb-12">
              <h3 className="text-2xl font-bold text-gray-900 sm:text-3xl">
                إحصائيات <span className="text-primary">المنصة</span>
              </h3>
              <p className="mt-4 text-lg text-gray-600">
                أرقام تعكس نجاح طلابنا وتفاعلهم مع المنصة
              </p>
            </div>

            {statisticsLoading ? (
              <StatisticsSkeleton />
            ) : statisticsError ? (
              <div className="text-center py-8">
                <p className="text-red-600">فشل في تحميل الإحصائيات</p>
                <p className="text-gray-600 text-sm mt-2">جاري استخدام الإحصائيات الافتراضية</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-6 lg:grid-cols-4">
                {stats.map((stat, index) => (
                  <div key={index} className="card-subtle p-6 rounded-2xl text-center hover:shadow-lg transition-all duration-300">
                    <div className={`text-3xl sm:text-4xl font-bold mb-3 ${
                      index === 0 ? 'text-primary' :
                      index === 1 ? 'text-blue-600' :
                      index === 2 ? 'text-green-600' :
                      'text-dark-yellow'
                    }`}>
                      {stat.number}
                    </div>
                    <div className="text-gray-600 font-medium text-sm sm:text-base">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Top Students Section */}
          <div className="mt-24">
            <div className="max-w-2xl mx-auto text-center mb-12">
              <h3 className="text-2xl font-bold text-gray-900 sm:text-3xl">
                أفضل <span className="text-primary">الطلاب</span>
              </h3>
              <p className="mt-4 text-lg text-gray-600">
                طلاب متميزون في منصتنا
              </p>
            </div>
            
            {topStudentsLoading ? (
              <TopStudentsSkeleton />
            ) : topStudentsError ? (
              <div className="text-center py-8">
                <p className="text-red-600">فشل في تحميل قائمة أفضل الطلاب</p>
                <p className="text-gray-600 text-sm mt-2">يرجى المحاولة مرة أخرى لاحقًا</p>
              </div>
            ) : !topStudentsData?.success ? (
              <div className="text-center py-8">
                <p className="text-red-600">فشل في تحميل قائمة أفضل الطلاب</p>
                <p className="text-gray-600 text-sm mt-2">يرجى المحاولة مرة أخرى لاحقًا</p>
              </div>
            ) : (
              <>
                <p className="text-center mb-8 text-gray-600">
                  <span className="font-semibold">{topStudentsData.data.criteria}</span> - تم التحديث في {formatDate(topStudentsData.data.generatedAt)}
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {topStudentsData.data.students.map((student, index) => (
                    <div 
                      key={student.userId} 
                      className={`relative card-subtle p-6 rounded-2xl hover:shadow-lg transition-all duration-300 bg-gradient-to-br ${studentCardGradients[index % studentCardGradients.length]} border border-gray-100`}
                    >
                      {/* Rank Badge */}
                      <div className="absolute -top-3 -right-3 w-10 h-10 flex items-center justify-center rounded-full bg-white border-2 border-primary shadow-lg">
                        <FaTrophy className={`text-lg ${rankColors[student.rank] || 'text-primary'}`} />
                      </div>
                      
                      <div className="flex items-center mb-5">
                        <div className={`flex items-center justify-center w-14 h-14 rounded-full bg-white shadow-sm border-2 border-primary/20 text-xl font-bold text-primary mr-4`}>
                          {getInitial(student.userInfo.name)}
                        </div>
                        <div>
                          <h4 className="text-xl font-bold text-gray-900">{student.userInfo.name}</h4>
                          <p className="text-sm text-gray-600">@{student.userInfo.username}</p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-y-3 text-sm">
                        <div className="flex items-center">
                          <FaUserGraduate className="text-primary ml-2" />
                          <span className="text-gray-700">{student.userInfo.class}</span>
                        </div>
                        <div className="flex items-center">
                          <FaSchool className="text-primary ml-2" />
                          <span className="text-gray-700">{student.userInfo.school}</span>
                        </div>
                        <div className="flex items-center">
                          <span className="bg-green-100 text-green-800 text-xs px-2.5 py-0.5 rounded-full ml-2">أفضل درجة</span>
                          <span className="text-gray-700 font-semibold">{student.bestScore}%</span>
                        </div>
                        <div className="flex items-center">
                          <span className="bg-blue-100 text-blue-800 text-xs px-2.5 py-0.5 rounded-full ml-2">عدد المراجعات</span>
                          <span className="text-gray-700 font-semibold">{student.totalTests}</span>
                        </div>
                      </div>
                      
                      <div className="mt-4 pt-3 border-t border-gray-100 grid grid-cols-2 gap-2 text-xs">
                        <div className="flex items-center">
                          <FaCalendarAlt className="text-primary ml-1" />
                          <span className="text-gray-600">أول مراجعة: {formatDate(student.firstTestDate)}</span>
                        </div>
                        <div className="flex items-center">
                          <FaCalendarAlt className="text-primary ml-1" />
                          <span className="text-gray-600">آخر مراجعةصث: {formatDate(student.lastTestDate)}</span>
                        </div>
                      </div>
                      
                      {/* Rank indicator at the bottom */}
                      <div className="absolute bottom-3 left-3">
                        <span className={`text-xs font-bold ${rankColors[student.rank] || 'text-primary'}`}>
                          المركز #{student.rank}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* <div className="text-center mt-6 text-gray-600 text-sm">
                  <p>إجمالي الطلاب في المنصة: {topStudentsData.data.totalStudentsInSystem}</p>
                </div> */}
              </>
            )}
          </div>
        </div>
      </section>

      {/* Comments Section */}
      <section className="relative py-16 bg-gray-50">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center mb-16">
            <h2 className="text-3xl font-bold leading-tight text-gray-900 sm:text-4xl lg:text-5xl">
              آراء <span className="text-primary">الطلاب</span>
            </h2>
            <p className="max-w-lg mx-auto mt-4 text-lg leading-relaxed text-gray-600">
              ما يقوله طلابنا عن تجربتهم مع المنصة
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <FaSpinner className="animate-spin text-3xl text-primary" />
              <span className="mr-3 text-lg text-gray-600">جاري تحميل التعليقات...</span>
            </div>
          ) : ratings.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-lg text-gray-600">لا توجد تعليقات حتى الآن</p>
            </div>
          ) : (
            <>
              <div className="flex gap-6 pb-8 overflow-x-auto scroll-container">
                {ratings.map((rating, index) => {
                  const color = cardColors[index % cardColors.length];
                  return (
                    <div
                      key={rating._id}
                      className="scroll-item flex-shrink-0 w-80 sm:w-96 rounded-2xl p-6 shadow-sm border border-gray-100 bg-white hover:shadow-md transition-all duration-300"
                    >
                      <div className="flex items-start gap-4 mb-4">
                        <div
                          className={`flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br ${color} text-2xl font-bold flex-shrink-0`}
                        >
                          {getInitial(rating.username)}
                        </div>
                        <div className="flex-1">
                          <span className="inline-block px-3 py-1 text-xs font-medium border border-yellow-600 text-yellow-600 rounded-full mb-2">
                            تقييم
                          </span>
                          <h3 className="text-lg font-bold text-gray-900">
                            {rating.username || 'طالب'}
                          </h3>
                          <p className="text-sm text-gray-500 mt-1">
                            {formatDate(rating.createdAt)}
                          </p>
                        </div>
                      </div>
                      <p className="text-gray-600 leading-relaxed">
                        {rating.description}
                      </p>
                    </div>
                  );
                })}
              </div>

              {/* زر تحميل المزيد */}
              {pagination.hasNextPage && (
                <div className="text-center mt-8">
                  <button
                    onClick={loadMoreRatings}
                    disabled={loadingMore}
                    className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-opacity-90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center mx-auto"
                  >
                    {loadingMore ? (
                      <>
                        <FaSpinner className="animate-spin ml-2" />
                        جاري التحميل...
                      </>
                    ) : (
                      <>
                        <FaArrowLeft className="ml-2" />
                        تحميل المزيد من التعليقات
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* معلومات الصفحة */}
              <div className="text-center mt-6 text-gray-600">
                <p>
                  الصفحة {pagination.currentPage} من {pagination.totalPages} • 
                  إجمالي التعليقات: {pagination.totalRatings}
                </p>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Footer */}
      <Footer />

      <style jsx>{`
        .grid-pattern {
          background-image:
            linear-gradient(rgba(50, 10, 107, 0.02) 1px, transparent 1px),
            linear-gradient(90deg, rgba(50, 10, 107, 0.02) 1px, transparent 1px);
          background-size: 25px 25px;
          background-color: #f9fafb; /* نفس لون bg-gray-50 */
        }

        /* تحسينات للألوان والتباين */
        .text-primary {
          color: #320A6B;
        }

        .bg-primary {
          background-color: #320A6B;
        }

        .border-primary {
          border-color: #320A6B;
        }

        .text-dark-yellow {
          color: #B8860B;
        }

        .bg-dark-yellow {
          background-color: #B8860B;
        }

        /* تحسين التباين للنصوص على الخلفيات الملونة */
        .card-subtle {
          background: rgba(255, 255, 255, 0.95);
          border: 1px solid rgba(0, 0, 0, 0.08);
          backdrop-filter: blur(10px);
          transition: all 0.3s ease;
        }

        .scroll-container {
          scroll-snap-type: x mandatory;
          scrollbar-width: none;
        }

        .scroll-container::-webkit-scrollbar {
          display: none;
        }

        .scroll-item {
          scroll-snap-align: center;
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in-up {
          animation: fadeInUp 0.6s ease forwards;
        }
      `}</style>

      <style jsx global>{`
        body {
          font-family: 'Cairo', sans-serif;
        }

        html {
          scroll-behavior: smooth;
        }

        /* تأكد من أن جميع النصوص مرئية */
        .text-gray-600 {
          color: #4b5563;
        }

        .text-gray-900 {
          color: #111827;
        }

        .text-white {
          color: #ffffff;
        }
      `}</style>
    </div>
  );
};

export default HowItWorksPage;